
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { PEOProviderForm } from '@/components/admin/peo/PEOProviderForm';
import { usePEOProviders } from '@/components/admin/peo/hooks/usePEOProviders';
import { useEffect, useState } from 'react';
import type { PEOProvider } from '@/types/peo.types';

interface PEOProviderDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedProvider?: PEOProvider;
  onSuccess: () => void;
}

export const PEOProviderDialog = ({
  isOpen,
  onOpenChange,
  selectedProvider,
  onSuccess
}: PEOProviderDialogProps) => {
  const { handleProviderDelete, isDeletingProvider, refetch } = usePEOProviders();
  const [isClosePending, setIsClosePending] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsClosePending(false);
    }
  }, [isOpen]);

  const handleCloseDialog = () => {
    // Mark close as pending to prevent double-close issues
    setIsClosePending(true);
    
    // Refetch data to ensure we have the latest
    console.log('Dialog closing, forcing data refetch');
    refetch();
    
    // Call the onSuccess callback
    onSuccess();
    
    // Close dialog programmatically
    onOpenChange(false);
  };

  // Block accidental closures when not initiated by our close handler
  const handleOpenChange = (open: boolean) => {
    if (!open && !isClosePending) {
      const confirmClose = window.confirm('Are you sure you want to close without saving? Your changes will be lost.');
      if (confirmClose) {
        onOpenChange(false);
      }
    } else {
      onOpenChange(open);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {selectedProvider ? 'Edit PEO Provider' : 'Add PEO Provider'}
          </DialogTitle>
          <DialogDescription>
            {selectedProvider 
              ? `Update information for ${selectedProvider.name}`
              : 'Enter details for the new PEO provider'}
          </DialogDescription>
        </DialogHeader>
        <PEOProviderForm 
          provider={selectedProvider}
          onSuccess={handleCloseDialog}
          onClose={handleCloseDialog}
          onDeleteProvider={handleProviderDelete}
          isDeleteInProgress={isDeletingProvider}
        />
      </DialogContent>
    </Dialog>
  );
};
